/**
 * API client for PlanScan.io backend
 * Handles authentication headers, error responses, and automatic token refresh
 */

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000'
const TOKEN_KEY = 'planscan_token'
const REFRESH_TOKEN_KEY = 'planscan_refresh_token'

export interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
  statusCode?: number
}

// Token refresh state management
let isRefreshing = false
let refreshSubscribers: Array<(success: boolean) => void> = []

// Subscribe to token refresh completion
function subscribeToTokenRefresh(): Promise<boolean> {
  return new Promise((resolve) => {
    refreshSubscribers.push(resolve)
  })
}

// Notify all subscribers of refresh result
function notifyRefreshSubscribers(success: boolean) {
  refreshSubscribers.forEach((callback) => callback(success))
  refreshSubscribers = []
}

// Logout event - components can listen to this
export const authEvents = {
  listeners: new Set<() => void>(),
  onLogout(callback: () => void) {
    this.listeners.add(callback)
    return () => this.listeners.delete(callback)
  },
  emitLogout() {
    this.listeners.forEach((callback) => callback())
  },
}

// Attempt to refresh the access token
async function attemptTokenRefresh(): Promise<boolean> {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
  if (!refreshToken) {
    return false
  }

  try {
    const response = await fetch(`${API_URL}/api/auth/refresh-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    })

    if (!response.ok) {
      return false
    }

    const data = await response.json()
    if (data.accessToken && data.refreshToken) {
      localStorage.setItem(TOKEN_KEY, data.accessToken)
      localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken)
      return true
    }
    return false
  } catch {
    return false
  }
}

// Handle 401 errors with automatic token refresh
async function handleUnauthorized<T>(
  endpoint: string,
  options: RequestInit,
  skipAuth: boolean
): Promise<ApiResponse<T> | null> {
  // Don't try to refresh for auth endpoints
  if (skipAuth || endpoint.includes('/auth/')) {
    return null
  }

  // If already refreshing, wait for it to complete
  if (isRefreshing) {
    const success = await subscribeToTokenRefresh()
    if (success) {
      // Retry the original request with new token
      return request<T>(endpoint, options, true)
    }
    return null
  }

  // Start refreshing
  isRefreshing = true

  try {
    const success = await attemptTokenRefresh()

    if (success) {
      notifyRefreshSubscribers(true)
      // Retry the original request with new token
      return request<T>(endpoint, options, true)
    } else {
      notifyRefreshSubscribers(false)
      // Clear tokens and trigger logout
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(REFRESH_TOKEN_KEY)
      authEvents.emitLogout()
      return null
    }
  } finally {
    isRefreshing = false
  }
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {},
  isRetry = false
): Promise<ApiResponse<T>> {
  const token = localStorage.getItem(TOKEN_KEY)

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  }

  if (token) {
    ;(headers as Record<string, string>)['Authorization'] = `Bearer ${token}`
  }

  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    })

    const statusCode = response.status

    // Handle 401 with automatic token refresh (only if not already retrying)
    if (statusCode === 401 && !isRetry) {
      const retryResult = await handleUnauthorized<T>(endpoint, options, false)
      if (retryResult) {
        return retryResult
      }
      // If refresh failed, return the 401 error
      return {
        success: false,
        error: 'Your session has expired. Please sign in again.',
        statusCode: 401,
      }
    }

    // Handle non-JSON responses
    const contentType = response.headers.get('content-type')
    if (!contentType?.includes('application/json')) {
      if (!response.ok) {
        return {
          success: false,
          error: `Server error (${statusCode})`,
          statusCode,
        }
      }
      return { success: true, statusCode }
    }

    const data = await response.json()

    if (!response.ok) {
      // Handle Derek-friendly error messages
      const error = data.message || data.error || getDefaultError(statusCode)
      return { success: false, error, statusCode }
    }

    return { success: true, data, statusCode }
  } catch (err) {
    // Network error or other failure
    return {
      success: false,
      error: 'Unable to connect. Please check your internet connection.',
    }
  }
}

function getDefaultError(statusCode: number): string {
  switch (statusCode) {
    case 400:
      return 'Please check your input and try again.'
    case 401:
      return 'Please sign in to continue.'
    case 403:
      return 'You do not have permission to do this.'
    case 404:
      return 'The requested resource was not found.'
    case 429:
      return 'Too many requests. Please wait a moment and try again.'
    case 500:
    case 502:
    case 503:
      return 'Something went wrong on our end. Please try again.'
    default:
      return 'An unexpected error occurred. Please try again.'
  }
}

export const api = {
  get: <T>(endpoint: string, options?: RequestInit) =>
    request<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, body?: unknown, options?: RequestInit) =>
    request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),

  put: <T>(endpoint: string, body?: unknown, options?: RequestInit) =>
    request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    }),

  patch: <T>(endpoint: string, body?: unknown, options?: RequestInit) =>
    request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    }),

  delete: <T>(endpoint: string, options?: RequestInit) =>
    request<T>(endpoint, { ...options, method: 'DELETE' }),
}
